import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import cx from "classnames";
import InputFrame from "../InputFrame";
import styles from "./DropDown.module.scss";
import reactSelectOverWriteStyles from "./reactSelectOverWriteStyles";

const DropDown = forwardRef(function DropDown(
  {
    label,
    className,
    value,
    defaultValue,
    error,
    placeholder,
    errorMessage,
    options,
    multiple,
    requiredInput,
    withInfoIcon,
    labelPosition,
    disabled,
    searchable,
    onInputChange,
    onChange,
    clearable,
    getOptionLabel,
    getOptionValue,
    onMenuOpen,
    onMenuClose,
    inputClass,
    ...rest
  },
  ref
) {
  return (
    <InputFrame
      label={label}
      disabled={disabled}
      className={className}
      error={error}
      labelPosition={labelPosition}
      requiredInput={requiredInput}
      withInfoIcon={withInfoIcon}
      errorMessage={errorMessage}
      inputClass={inputClass}
    >
      {({ inputClassName }) => (
        <Select
          className={cx(inputClassName, styles.container)}
          styles={reactSelectOverWriteStyles}
          options={options}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          onInputChange={onInputChange}
          isSearchable={searchable}
          isClearable={clearable}
          isMulti={multiple}
          isDisabled={disabled}
          placeholder={placeholder}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
          {...rest}
          ref={ref}
        />
      )}
    </InputFrame>
  );
});

const OptionType = PropTypes.shape({
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.object]).isRequired
});

DropDown.propTypes = {
  /** Dropdown id. */
  id: PropTypes.string,
  /** Dropdown name. */
  name: PropTypes.string,
  /** Is the dropdown disabled. */
  disabled: PropTypes.bool,
  /** Available values are 'top' & 'left' */
  labelPosition: PropTypes.oneOf(["left", "top"]),
  /** An errored dropdown can alert a user to a problem. */
  error: PropTypes.bool,
  /** Errored dropdown error message. */
  errorMessage: PropTypes.string,
  /** If provided, will add * after the label */
  requiredInput: PropTypes.bool,
  /** Dropdown label. */
  label: PropTypes.string,
  /** Placeholder of input. */
  placeholder: PropTypes.string,
  /** Current value or value array if multiple. Creates a controlled component. */
  value: PropTypes.oneOfType([PropTypes.arrayOf(OptionType), OptionType]),
  /** Array of Dropdown items. */
  defaultValue: PropTypes.oneOfType([PropTypes.arrayOf(OptionType), OptionType]),
  /** Additional classes. */
  className: PropTypes.string,
  /** Array of Dropdown items. */
  options: PropTypes.arrayOf(OptionType),
  /** A dropdown can allow multiple selections. */
  multiple: PropTypes.bool,
  /** Is the dropdown value clearable */
  clearable: PropTypes.bool,
  /** Whether to enable search functionality */
  searchable: PropTypes.bool,
  /** Is the dropdown in a state of loading */
  loading: PropTypes.bool,
  /** If provided, will add information icon after the label */
  withInfoIcon: PropTypes.bool,
  /** Handle change events on the dropdown */
  onChange: PropTypes.func,
  /** Provide custom label */
  getOptionLabel: PropTypes.func,
  /** Provide custom value */
  getOptionValue: PropTypes.func,
  /** Handle opening of menu */
  onMenuOpen: PropTypes.func,
  /** Handle closing of menu */
  onMenuClose: PropTypes.func,
  /** Handle change events of the input */
  onInputChange: PropTypes.func,
  /** input class */
  inputClass: PropTypes.string
};

DropDown.defaultProps = {
  id: undefined,
  name: undefined,
  error: false,
  withInfoIcon: false,
  disabled: false,
  errorMessage: null,
  getOptionLabel: undefined,
  getOptionValue: undefined,
  onMenuOpen: undefined,
  onMenuClose: undefined,
  label: null,
  className: null,
  value: undefined,
  placeholder: undefined,
  options: [],
  defaultValue: undefined,
  multiple: false,
  requiredInput: false,
  clearable: false,
  searchable: false,
  labelPosition: "top",
  onChange: undefined,
  onInputChange: undefined,
  loading: false,
  inputClass: ""
};

export default DropDown;
